import { SQLGeneratedProcess } from "../../../shared-types/dist/SQLGeneratedProcess";
import { ProcessTableRow } from "./ProcessTableRow";
import { ProcessTableRowHeader } from "./ProcessTableRowHeader";
import { TableRow } from "./ProcessTableUtils";
import {
  Table as AvenueTable,
  TableHeader as AvenueTableHeader,
  TableHead as AvenueTableHead,
  TableRow as AvenueTableRow,
  TableCell as AvenueTableCell,
  TableBody as AvenueTableBody,
} from "@avenueops/web-components";
import { Cell, flexRender, Header, Row, Table } from "@tanstack/react-table";
import React from "react";
import { useVirtual } from "react-virtual";

interface Props {
  table: Table<TableRow>;
  fetchMoreOnBottomReached: (target: HTMLDivElement) => void;
  selectedProcess: SQLGeneratedProcess | null;
  setSelectedProcess: (_: SQLGeneratedProcess | null) => void;
}

export function ProcessTable({ table, fetchMoreOnBottomReached, selectedProcess, setSelectedProcess }: Props) {
  //we need a reference to the scrolling element for logic down below
  const tableContainerRef = React.useRef<HTMLDivElement>(null);

  //a check on mount and after a fetch to see if the table is already scrolled to the bottom and immediately needs to fetch more data
  React.useEffect(() => {
    if (tableContainerRef.current) {
      fetchMoreOnBottomReached(tableContainerRef.current);
    }
  }, [fetchMoreOnBottomReached]);

  const { rows } = table.getRowModel();

  // Virtualizing is optional, but might be necessary if we are going to potentially have hundreds or thousands of rows
  const rowVirtualizer = useVirtual({
    parentRef: tableContainerRef,
    size: rows.length,
    overscan: 20,
  });

  const { virtualItems: virtualRows, totalSize } = rowVirtualizer;
  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom = virtualRows.length > 0 ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0;

  return (
    <div
      className="relative z-0 overflow-auto rounded  h-full w-full align-middle bg-white "
      onScroll={(e) => fetchMoreOnBottomReached(e.target as HTMLDivElement)}
      ref={tableContainerRef}
    >
      <AvenueTable className="table-fixed w-full">
        <AvenueTableHeader className="sticky top-0 z-50">
          {table.getHeaderGroups().map((headerGroup) => (
            <AvenueTableRow key={headerGroup.id}>{headerGroup.headers.map(renderHeader)}</AvenueTableRow>
          ))}
        </AvenueTableHeader>
        <AvenueTableBody>
          {paddingTop > 0 && (
            <AvenueTableRow key={"topPadding"}>
              <AvenueTableCell key="paddingCellTop" style={{ height: `${paddingTop}px` }} />
            </AvenueTableRow>
          )}
          {virtualRows.map((virtualRow) => renderSignalRow(rows[virtualRow.index], selectedProcess, setSelectedProcess))}
          {paddingBottom > 0 && (
            <AvenueTableRow key={"bottomPadding"}>
              <AvenueTableCell key="paddingCellBottom" style={{ height: `${paddingBottom}px` }} />
            </AvenueTableRow>
          )}
        </AvenueTableBody>
      </AvenueTable>
    </div>
  );
}

/**
 * This is a custom render function for the header. It is necessary to make the header sticky and to add some padding to the first and last columns
 * @param header - the header object
 * @returns the rendered header.
 */
function renderHeader(header: Header<TableRow, unknown>) {
  return header.isPlaceholder ? null : (
    <AvenueTableHead className="sticky top-0" key={header.id} colSpan={header.colSpan} style={{ width: header.getSize() }}>
      <div>{flexRender(header.column.columnDef.header, header.getContext())}</div>
      {header.column.getCanResize() ? (
        <div
          {...{
            onMouseDown: header.getResizeHandler(),
            onTouchStart: header.getResizeHandler(),
            className: `resizer ${header.column.getIsResizing() ? "isResizing" : ""}`,
          }}
        />
      ) : undefined}
    </AvenueTableHead>
  );
}

/**
 * This is the row renderer. It is a function that takes a row and returns a React element.
 * @param row The row to render.
 * @returns The rendered row.
 */
function renderSignalRow(row: Row<TableRow>, selectedProcess: SQLGeneratedProcess | null, setSelectedProcess: (_: SQLGeneratedProcess | null) => void) {
  return row?.original.header ? (
    row.original.header.title !== "All" ? (
      <ProcessTableRowHeader
        key={row.id}
        rowId={row.id}
        title={row.original.header.title}
        icon={row.original.header.icon}
        count={row.original.header.count}
        span={row.getAllCells().length}
      />
    ) : undefined
  ) : (
    <ProcessTableRow key={row.id} tableRow={row} selectedProcess={selectedProcess} setSelectedProcess={setSelectedProcess}>
      {row.getVisibleCells().map(renderSignalCell)}
    </ProcessTableRow>
  );
}

/**
 * This is the cell renderer. It is a function that takes a cell and returns a React element.
 * @param cell The cell to render
 * @returns The rendered cell.
 */
function renderSignalCell(cell: Cell<TableRow, unknown>) {
  return <AvenueTableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</AvenueTableCell>;
}
